/**
 * Team Service
 * Handles team-related database operations
 */

import { BaseService } from './BaseService.js'
import { COLLECTIONS, SCHEMAS } from '../config.js'

export class TeamService extends BaseService {
  constructor() {
    super(COLLECTIONS.TEAMS)
  }

  /**
   * Create a new team
   * @param {Object} teamData - Team data
   * @returns {Promise<Object>} Created team
   */
  async createTeam(teamData) {
    const validation = this.validateData(teamData, SCHEMAS[COLLECTIONS.TEAMS])
    if (!validation.isValid) {
      throw new Error(`Validation failed: ${validation.errors.join(', ')}`)
    }

    // Initialize empty arrays if not provided
    if (!teamData.members) {
      teamData.members = []
    }
    if (!teamData.memberRoles) {
      teamData.memberRoles = {}
    }

    return await this.create(teamData)
  }

  /**
   * Update team
   * @param {string} teamId - Team ID
   * @param {Object} updateData - Update data
   * @returns {Promise<Object>} Updated team
   */
  async updateTeam(teamId, updateData) {
    const validation = this.validateData(updateData, SCHEMAS[COLLECTIONS.TEAMS])
    if (!validation.isValid) {
      throw new Error(`Validation failed: ${validation.errors.join(', ')}`)
    }

    return await this.update(teamId, updateData)
  }

  /**
   * Add member to team
   * @param {string} teamId - Team ID
   * @param {string} userId - User ID
   * @param {string} role - Member role (optional)
   * @returns {Promise<Object>} Updated team
   */
  async addMember(teamId, userId, role = 'member') {
    const team = await this.getById(teamId)
    if (!team) {
      throw new Error('Team not found')
    }

    if (!team.members.includes(userId)) {
      team.members.push(userId)
      team.memberRoles[userId] = role
      
      // Update team
      const updatedTeam = await this.update(teamId, {
        members: team.members,
        memberRoles: team.memberRoles
      })
      
      // CRITICAL: Also update user's teams array (bidirectional relationship)
      try {
        const { UserService } = await import('./UserService.js')
        const userService = new UserService()
        const user = await userService.getById(userId)
        if (user) {
          const userTeams = user.teams || []
          if (!userTeams.includes(teamId)) {
            await userService.update(userId, {
              teams: [...userTeams, teamId]
            })
          }
        }
      } catch (error) {
        // Log error but don't fail the operation
        console.warn('Failed to update user teams array:', error)
      }
      
      // Add user to all projects in this team
      await this._addUserToTeamProjects(teamId, userId)
      
      return updatedTeam
    }

    return team
  }
  
  /**
   * Helper method to add a user to all projects in a team
   * Marks user as team-level access in all projects
   * @param {string} teamId - Team ID
   * @param {string} userId - User ID
   * @private
   */
  async _addUserToTeamProjects(teamId, userId) {
    try {
      
      // Import ProjectService and UserService
      const { ProjectService } = await import('./ProjectService.js')
      const { UserService } = await import('./UserService.js')
      
      const projectService = new ProjectService()
      const userService = new UserService()
      
      // Get all projects for this team
      const teamProjects = await projectService.getByTeam(teamId)
      
      if (!teamProjects || teamProjects.length === 0) {
        return // No projects to add user to
      }
      
      // Get the user
      const user = await userService.getById(userId)
      if (!user) {
        console.warn(`⚠️ [Auto-Add] User ${userId} not found, cannot add to projects`)
        return // User not found
      }
      
      let successCount = 0
      let errorCount = 0
      
      // Update each project to include this user as team-level member
      for (const project of teamProjects) {
        try {
          // Fetch fresh project data to ensure we have the latest state
          const freshProject = await projectService.getById(project.id)
          if (!freshProject) {
            console.warn(`⚠️ [Auto-Add] Project ${project.id} not found, skipping`)
            errorCount++
            continue
          }
          
          const projectMembers = freshProject.members || []
          const memberAccessTypes = freshProject.memberAccessTypes || {}
          
          // Add user to project members if not already present
          if (!projectMembers.includes(userId)) {
            projectMembers.push(userId)
          }
          
          // Mark as team-level access (overwrites project-level if exists)
          memberAccessTypes[userId] = 'team'
          
          // Update project with members and access types
          const updateResult = await projectService.update(project.id, {
            members: projectMembers,
            memberAccessTypes: memberAccessTypes,
            updated_at: new Date()
          })
          
          if (updateResult) {
            successCount++
          } else {
            errorCount++
            console.warn(`⚠️ [Auto-Add] Update returned null for project ${project.id}`)
          }
        } catch (projectError) {
          // Continue with other projects even if one fails
          errorCount++
          console.error(`❌ [Auto-Add] Failed to add user ${userId} to project ${project.id}:`, projectError)
        }
      }
      
      
      // Update user's projects list to include all team projects
      const userProjects = user.projects || []
      const updatedProjects = [...new Set([...userProjects, ...teamProjects.map(p => p.id)])]
      
      await userService.update(userId, {
        projects: updatedProjects,
        updated_at: new Date()
      })
      
    } catch (error) {
      // Log error with details
      console.error(`❌ [Auto-Add] Failed to add user ${userId} to team ${teamId} projects:`, error)
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        teamId,
        userId
      })
    }
  }

  /**
   * Helper method to remove a user from all projects in a team
   * Removes user from project members and access types
   * @param {string} teamId - Team ID
   * @param {string} userId - User ID
   * @private
   */
  async _removeUserFromTeamProjects(teamId, userId) {
    try {
      
      // Import ProjectService and UserService
      const { ProjectService } = await import('./ProjectService.js')
      const { UserService } = await import('./UserService.js')
      
      const projectService = new ProjectService()
      const userService = new UserService()
      
      // Get all projects for this team
      const teamProjects = await projectService.getByTeam(teamId)
      console.log(`🔄 [Auto-Remove] Found ${teamProjects?.length || 0} project(s) for team ${teamId}`)
      
      if (!teamProjects || teamProjects.length === 0) {
        console.log(`ℹ️ [Auto-Remove] No projects found for team ${teamId}, nothing to remove`)
        return // No projects to remove user from
      }
      
      // Get the user
      const user = await userService.getById(userId)
      if (!user) {
        console.warn(`⚠️ [Auto-Remove] User ${userId} not found, cannot remove from projects`)
        return // User not found
      }
      
      let successCount = 0
      let errorCount = 0
      const projectIdsToRemove = []
      
      // Remove user from each project
      for (const project of teamProjects) {
        try {
          // Fetch fresh project data to ensure we have the latest state
          const freshProject = await projectService.getById(project.id)
          if (!freshProject) {
            console.warn(`⚠️ [Auto-Remove] Project ${project.id} not found, skipping`)
            errorCount++
            continue
          }
          
          const projectMembers = freshProject.members || []
          const memberAccessTypes = freshProject.memberAccessTypes || {}
          
          // Remove user from project members if present
          if (projectMembers.includes(userId)) {
            const updatedMembers = projectMembers.filter(id => id !== userId)
            
            // Remove from access types if present
            if (memberAccessTypes[userId]) {
              delete memberAccessTypes[userId]
            }
            
            // Update project
            const updateResult = await projectService.update(project.id, {
              members: updatedMembers,
              memberAccessTypes: memberAccessTypes,
              updated_at: new Date()
            })
            
            if (updateResult) {
              successCount++
              projectIdsToRemove.push(project.id)
            } else {
              errorCount++
              console.warn(`⚠️ [Auto-Remove] Update returned null for project ${project.id}`)
            }
          } else {
          }
        } catch (projectError) {
          // Continue with other projects even if one fails
          errorCount++
          console.error(`❌ [Auto-Remove] Failed to remove user ${userId} from project ${project.id}:`, projectError)
        }
      }
      
      console.log(`📊 [Auto-Remove] Summary: ${successCount} succeeded, ${errorCount} failed out of ${teamProjects.length} projects`)
      
      // Update user's projects list to remove team projects
      const userProjects = user.projects || []
      const updatedProjects = userProjects.filter(projectId => !projectIdsToRemove.includes(projectId))
      
      await userService.update(userId, {
        projects: updatedProjects,
        updated_at: new Date()
      })
      
    } catch (error) {
      // Log error with details
      console.error(`❌ [Auto-Remove] Failed to remove user ${userId} from team ${teamId} projects:`, error)
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        teamId,
        userId
      })
    }
  }

  /**
   * Remove member from team
   * @param {string} teamId - Team ID
   * @param {string} userId - User ID
   * @returns {Promise<Object>} Updated team
   */
  async removeMember(teamId, userId) {
    const team = await this.getById(teamId)
    if (!team) {
      throw new Error('Team not found')
    }

    team.members = team.members.filter(id => id !== userId)
    delete team.memberRoles[userId]
    
    // CRITICAL: Also update user's teams array (bidirectional relationship)
    try {
      const { UserService } = await import('./UserService.js')
      const userService = new UserService()
      const user = await userService.getById(userId)
      if (user) {
        const userTeams = (user.teams || []).filter(id => id !== teamId)
        await userService.update(userId, {
          teams: userTeams
        })
      }
    } catch (error) {
      // Log error but don't fail the operation
      console.warn('Failed to update user teams array:', error)
    }
    
    // REQUIREMENT: Remove user from all projects in this team
    await this._removeUserFromTeamProjects(teamId, userId)
    
    return await this.update(teamId, {
      members: team.members,
      memberRoles: team.memberRoles
    })
  }

  /**
   * Bulk add member to multiple teams (optimized)
   * @param {Array<string>} teamIds - Array of team IDs
   * @param {string} userId - User ID
   * @param {string} role - Member role (optional)
   * @returns {Promise<Object>} Results object with success and error counts
   */
  async bulkAddMember(teamIds, userId, role = 'member') {
    await this._ensureDatabaseService()
    
    // Use HTTP adapter's direct API call if available
    if (this.db.adapter && this.db.adapter.baseUrl) {
      const response = await fetch(`${this.db.adapter.baseUrl}/teams/bulk-add-member`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ teamIds, userId, role })
      })
      
      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to bulk add member')
      }
      
      return await response.json()
    }
    
    // Fallback: Process individually if no HTTP adapter
    const results = []
    const errors = []
    
    for (const teamId of teamIds) {
      try {
        await this.addMember(teamId, userId, role)
        results.push({ teamId, status: 'added' })
      } catch (error) {
        errors.push({ teamId, error: error.message })
      }
    }
    
    // CRITICAL: After adding user to all teams, ensure user's teams array is updated
    // This handles the case where individual addMember calls might not have updated the user
    try {
      const { UserService } = await import('./UserService.js')
      const userService = new UserService()
      const user = await userService.getById(userId)
      if (user) {
        const userTeams = new Set(user.teams || [])
        teamIds.forEach(teamId => userTeams.add(teamId))
        await userService.update(userId, {
          teams: Array.from(userTeams)
        })
      }
    } catch (error) {
      console.warn('Failed to update user teams array in bulkAddMember:', error)
    }
    
    return {
      success: true,
      results,
      errors: errors.length > 0 ? errors : undefined,
      message: `Successfully processed ${teamIds.length} teams`
    }
  }

  /**
   * Bulk remove member from multiple teams (optimized)
   * @param {Array<string>} teamIds - Array of team IDs
   * @param {string} userId - User ID
   * @returns {Promise<Object>} Results object with success and error counts
   */
  async bulkRemoveMember(teamIds, userId) {
    await this._ensureDatabaseService()
    
    // Use HTTP adapter's direct API call if available
    if (this.db.adapter && this.db.adapter.baseUrl) {
      const response = await fetch(`${this.db.adapter.baseUrl}/teams/bulk-remove-member`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ teamIds, userId })
      })
      
      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to bulk remove member')
      }
      
      return await response.json()
    }
    
    // Fallback: Process individually if no HTTP adapter
    // This will call removeMember() which already handles project removal
    const results = []
    const errors = []
    
    for (const teamId of teamIds) {
      try {
        // removeMember() will automatically remove user from all team projects
        await this.removeMember(teamId, userId)
        results.push({ teamId, status: 'removed' })
      } catch (error) {
        errors.push({ teamId, error: error.message })
      }
    }
    
    return {
      success: true,
      results,
      errors: errors.length > 0 ? errors : undefined,
      message: `Successfully processed ${teamIds.length} teams`
    }
  }

  /**
   * Update member role
   * @param {string} teamId - Team ID
   * @param {string} userId - User ID
   * @param {string} role - New role
   * @returns {Promise<Object>} Updated team
   */
  async updateMemberRole(teamId, userId, role) {
    const team = await this.getById(teamId)
    if (!team) {
      throw new Error('Team not found')
    }

    if (!team.members.includes(userId)) {
      throw new Error('User is not a member of this team')
    }

    team.memberRoles[userId] = role
    return await this.update(teamId, { memberRoles: team.memberRoles })
  }

  /**
   * Get team members
   * @param {string} teamId - Team ID
   * @returns {Promise<Array>} Array of member IDs
   */
  async getMembers(teamId) {
    const team = await this.getById(teamId)
    if (!team) {
      throw new Error('Team not found')
    }

    // Get members from both members array and memberRoles object
    const membersFromArray = team.members || []
    const membersFromRoles = Object.keys(team.memberRoles || {})
    
    // Combine and deduplicate
    const allMembers = [...new Set([...membersFromArray, ...membersFromRoles])]
    
    return allMembers
  }

  /**
   * Get team member roles
   * @param {string} teamId - Team ID
   * @returns {Promise<Object>} Member roles mapping
   */
  async getMemberRoles(teamId) {
    const team = await this.getById(teamId)
    if (!team) {
      throw new Error('Team not found')
    }

    return team.memberRoles
  }

  /**
   * Get teams by member
   * @param {string} userId - User ID
   * @param {Object} options - Query options
   * @returns {Promise<Array>} Array of teams
   */
  async getByMember(userId, options = {}) {
    return await this.findBy('members', userId, options)
  }

  /**
   * Get teams by creator
   * @param {string} userId - User ID
   * @param {Object} options - Query options
   * @returns {Promise<Array>} Array of teams
   */
  async getByCreator(userId, options = {}) {
    return await this.findBy('created_by', userId, options)
  }

  /**
   * Search teams
   * @param {string} searchTerm - Search term
   * @param {Object} options - Search options
   * @returns {Promise<Array>} Array of matching teams
   */
  async searchTeams(searchTerm, options = {}) {
    const searchOptions = {
      fields: ['name', 'description'],
      ...options
    }
    return await this.search(searchTerm, searchOptions)
  }

  /**
   * Get team statistics
   * @param {string} teamId - Team ID
   * @returns {Promise<Object>} Team statistics
   */
  async getStatistics(teamId) {
    const team = await this.getById(teamId)
    if (!team) {
      throw new Error('Team not found')
    }

    return {
      id: team.id,
      name: team.name,
      memberCount: team.members.length,
      roleDistribution: this.getRoleDistribution(team.memberRoles),
      createdBy: team.created_by,
      createdAt: team.created_at,
      lastUpdated: new Date()
    }
  }

  /**
   * Get role distribution
   * @param {Object} memberRoles - Member roles mapping
   * @returns {Object} Role distribution
   */
  getRoleDistribution(memberRoles: Record<string, string> = {}) {
    const distribution: Record<string, number> = {}
    Object.values(memberRoles).forEach(role => {
      distribution[role] = (distribution[role] || 0) + 1
    })
    return distribution
  }

  /**
   * Get all team statistics
   * @returns {Promise<Object>} All teams statistics
   */
  async getAllStatistics() {
    const teams = await this.getAll()
    const totalMembers = teams.reduce((sum, team) => sum + team.members.length, 0)
    const averageMembersPerTeam = teams.length > 0 ? totalMembers / teams.length : 0

    return {
      totalTeams: teams.length,
      totalMembers,
      averageMembersPerTeam: Math.round(averageMembersPerTeam * 100) / 100,
      lastUpdated: new Date()
    }
  }

  /**
   * Soft delete a team with cascade to all its resources
   * @param {string} teamId - Team ID
   * @param {string} deletedBy - User ID who deleted the team
   * @param {Object} options - Deletion options
   * @param {boolean} options.cascadeToResources - Whether to cascade delete to resources (default: true)
   * @returns {Promise<Object>} Deletion summary
   */
  async softDelete(teamId, deletedBy, options: { cascadeToResources?: boolean } = {}) {
    const { cascadeToResources = true } = options
    
    
    try {
      // Get the team first
      const team = await this.getById(teamId)
      if (!team) {
        throw new Error('Team not found')
      }
      
      if (team.is_deleted) {
        throw new Error('Team is already deleted')
      }
      
      // Soft delete the team
      await this.update(teamId, {
        is_deleted: true,
        deleted_at: new Date(),
        deleted_by: deletedBy
      })
      
      
      const summary = {
        teamId,
        teamName: team.name,
        deletedAt: new Date(),
        deletedBy,
        resourcesDeleted: {
          projects: 0,
          features: 0,
          testCases: 0,
          testRuns: 0,
          bugs: 0,
          folders: 0
        }
      }
      
      // Cascade soft delete to all resources
      if (cascadeToResources) {
        
        // Import project service
        const { projectService } = await import('./index.js')
        
        // Get all projects in this team
        const projects = await projectService.getByTeam(teamId)
        const activeProjects = projects.filter(p => !p.is_deleted)
        
        // Collect all resource IDs across all projects first
        const allProjectIds = activeProjects.map(p => p.id)
        
        if (allProjectIds.length > 0) {
          // Import all required services
          const { 
            featureService, 
            testCaseService, 
            testRunService, 
            bugService,
            folderService 
          } = await import('./index.js')
          
          // Fetch all resources for all projects in batch
          const [allFeatures, allTestCases, allTestRuns, allBugs, allFolders] = await Promise.all([
            featureService.findByIn('project_id', allProjectIds),
            testCaseService.findByIn('project_id', allProjectIds),
            testRunService.findByIn('project_id', allProjectIds),
            bugService.findByIn('project_id', allProjectIds),
            folderService.findByIn('project_id', allProjectIds)
          ])
          
          // Filter out already deleted items
          const activeFeatures = allFeatures.filter(f => !f.is_deleted)
          const activeTestCases = allTestCases.filter(tc => !tc.is_deleted)
          const activeTestRuns = allTestRuns.filter(tr => !tr.is_deleted)
          const activeBugs = allBugs.filter(b => !b.is_deleted)
          const activeFolders = allFolders.filter(f => !f.is_deleted)
          
          // Prepare batch operations for all resources at once
          const allOperations = []
          
          // 1. Soft delete all projects
          allProjectIds.forEach(projectId => {
            allOperations.push({
              type: 'update',
              collection: 'projects',
              id: projectId,
              data: {
                is_deleted: true,
                deleted_at: new Date(),
                deleted_by: deletedBy,
                updated_at: new Date()
              }
            })
          })
          
          // 2. Soft delete all features
          activeFeatures.forEach(feature => {
            allOperations.push({
              type: 'update',
              collection: 'features',
              id: feature.id,
              data: {
                is_deleted: true,
                deleted_at: new Date(),
                deleted_by: deletedBy,
                updated_at: new Date()
              }
            })
          })
          
          // 3. Soft delete all test cases
          activeTestCases.forEach(testCase => {
            allOperations.push({
              type: 'update',
              collection: 'test_cases',
              id: testCase.id,
              data: {
                is_deleted: true,
                deleted_at: new Date(),
                deleted_by: deletedBy,
                updated_at: new Date()
              }
            })
          })
          
          // 4. Soft delete all test runs
          activeTestRuns.forEach(testRun => {
            allOperations.push({
              type: 'update',
              collection: 'test_runs',
              id: testRun.id,
              data: {
                is_deleted: true,
                deleted_at: new Date(),
                deleted_by: deletedBy,
                updated_at: new Date()
              }
            })
          })
          
          // 5. Soft delete all bugs
          activeBugs.forEach(bug => {
            allOperations.push({
              type: 'update',
              collection: 'bugs',
              id: bug.id,
              data: {
                is_deleted: true,
                deleted_at: new Date(),
                deleted_by: deletedBy,
                updated_at: new Date()
              }
            })
          })
          
          // 6. Soft delete all folders
          activeFolders.forEach(folder => {
            allOperations.push({
              type: 'update',
              collection: 'folders',
              id: folder.id,
              data: {
                ...folder,
                is_deleted: true,
                deleted_at: new Date(),
                deleted_by: deletedBy,
                updated_at: new Date()
              }
            })
          })
          
          // Execute all operations in a single batch call
          if (allOperations.length > 0) {
            await teamService.batch(allOperations)
          }
          
          // Update summary counts
          summary.resourcesDeleted.projects = activeProjects.length
          summary.resourcesDeleted.features = activeFeatures.length
          summary.resourcesDeleted.testCases = activeTestCases.length
          summary.resourcesDeleted.testRuns = activeTestRuns.length
          summary.resourcesDeleted.bugs = activeBugs.length
          summary.resourcesDeleted.folders = activeFolders.length
        }
        
      }
      
      return summary
      
    } catch (error) {
      throw error
    }
  }

  /**
   * Restore a soft-deleted team
   * @param {string} teamId - Team ID
   * @param {string} restoredBy - User ID who restored the team
   * @param {Object} options - Restoration options
   * @param {boolean} options.restoreResources - Whether to restore resources (default: true)
   * @returns {Promise<Object>} Restoration summary
   */
  async restore(teamId, restoredBy, options: { restoreResources?: boolean } = {}) {
    const { restoreResources = true } = options
    
    
    try {
      // Get the team first
      const team = await this.getById(teamId)
      if (!team) {
        throw new Error('Team not found')
      }
      
      if (!team.is_deleted) {
        throw new Error('Team is not deleted')
      }
      
      // Restore the team
      await this.update(teamId, {
        is_deleted: false,
        deleted_at: null,
        deleted_by: null,
        updated_at: new Date()
      })
      
      
      const summary = {
        teamId,
        teamName: team.name,
        restoredAt: new Date(),
        restoredBy,
        resourcesRestored: {
          projects: 0,
          features: 0,
          testCases: 0,
          testRuns: 0,
          bugs: 0,
          folders: 0
        }
      }
      
      // Restore all resources using single batch
      if (restoreResources) {
        
        // Import project service
        const { projectService } = await import('./index.js')
        
        // Get all deleted projects in this team
        const allProjects = await projectService.getByTeam(teamId)
        const deletedProjects = allProjects.filter(p => p.is_deleted)
        
        if (deletedProjects.length > 0) {
          const projectIds = deletedProjects.map(p => p.id)
          
          // Import all required services
          const { 
            featureService, 
            testCaseService, 
            testRunService, 
            bugService,
            folderService 
          } = await import('./index.js')
          
          // Fetch all resources for all projects in batch
          const [allFeatures, allTestCases, allTestRuns, allBugs, allFolders] = await Promise.all([
            featureService.findByIn('project_id', projectIds),
            testCaseService.findByIn('project_id', projectIds),
            testRunService.findByIn('project_id', projectIds),
            bugService.findByIn('project_id', projectIds),
            folderService.findByIn('project_id', projectIds)
          ])
          
          // Filter for deleted items only
          const deletedFeatures = allFeatures.filter(f => f.is_deleted)
          const deletedTestCases = allTestCases.filter(tc => tc.is_deleted)
          const deletedTestRuns = allTestRuns.filter(tr => tr.is_deleted)
          const deletedBugs = allBugs.filter(b => b.is_deleted)
          const deletedFolders = allFolders.filter(f => f.is_deleted)
          
          // Prepare batch operations for all resources at once
          const allOperations = []
          
          // 1. Restore all projects
          projectIds.forEach(projectId => {
            allOperations.push({
              type: 'update',
              collection: 'projects',
              id: projectId,
              data: {
                is_deleted: false,
                deleted_at: null,
                deleted_by: null,
                updated_at: new Date()
              }
            })
          })
          
          // 2. Restore all features
          deletedFeatures.forEach(feature => {
            allOperations.push({
              type: 'update',
              collection: 'features',
              id: feature.id,
              data: {
                is_deleted: false,
                deleted_at: null,
                deleted_by: null,
                updated_at: new Date()
              }
            })
          })
          
          // 3. Restore all test cases
          deletedTestCases.forEach(testCase => {
            allOperations.push({
              type: 'update',
              collection: 'test_cases',
              id: testCase.id,
              data: {
                is_deleted: false,
                deleted_at: null,
                deleted_by: null,
                updated_at: new Date()
              }
            })
          })
          
          // 4. Restore all test runs
          deletedTestRuns.forEach(testRun => {
            allOperations.push({
              type: 'update',
              collection: 'test_runs',
              id: testRun.id,
              data: {
                is_deleted: false,
                deleted_at: null,
                deleted_by: null,
                updated_at: new Date()
              }
            })
          })
          
          // 5. Restore all bugs
          deletedBugs.forEach(bug => {
            allOperations.push({
              type: 'update',
              collection: 'bugs',
              id: bug.id,
              data: {
                is_deleted: false,
                deleted_at: null,
                deleted_by: null,
                updated_at: new Date()
              }
            })
          })
          
          // 6. Restore all folders
          deletedFolders.forEach(folder => {
            allOperations.push({
              type: 'update',
              collection: 'folders',
              id: folder.id,
              data: {
                ...folder,
                is_deleted: false,
                deleted_at: null,
                deleted_by: null,
                updated_at: new Date()
              }
            })
          })
          
          // Execute all operations in a single batch call
          if (allOperations.length > 0) {
            await teamService.batch(allOperations)
          }
          
          // Update summary counts
          summary.resourcesRestored.projects = deletedProjects.length
          summary.resourcesRestored.features = deletedFeatures.length
          summary.resourcesRestored.testCases = deletedTestCases.length
          summary.resourcesRestored.testRuns = deletedTestRuns.length
          summary.resourcesRestored.bugs = deletedBugs.length
          summary.resourcesRestored.folders = deletedFolders.length
        }
        
      }
      
      return summary
      
    } catch (error) {
      throw error
    }
  }

  /**
   * Get deleted teams
   * @param {Object} options - Query options
   * @returns {Promise<Array>} Array of deleted teams
   */
  async getDeleted(options = {}) {
    const allTeams = await this.getAll(options)
    return allTeams.filter(team => team.is_deleted === true)
  }
}

export const teamService = new TeamService()
